import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { VolumeX } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import {
  TRANSITION_TYPES,
  effectiveTransitionMs,
  type Clip,
  type Transition,
} from "@rec/shared";
import {
  clipDurationMs,
  moveClip,
  outputOffsets,
  snap,
  totalDurationMs,
  trimClip,
} from "@/lib/timeline";
import { cn } from "@/lib/utils";
import { useFormat } from "@/i18n/format";

/**
 * The timeline: clips as blocks on a zoomable strip.
 *
 * Three gestures, all pointer-math rather than a drag library, because a
 * timeline's drags are constrained to one axis with snapping and live preview —
 * exactly the cases generic dnd abstractions fight you on:
 *
 *  - drag a clip's body        → reorder (drop indicator shows the seam)
 *  - drag a clip's edge        → trim, snapped to neighbours and the playhead
 *  - click / drag the ruler    → seek
 *
 * Gestures preview locally and commit once, on release — the parent receives
 * one `onChange` per completed gesture, which is what makes undo one step per
 * human action instead of one step per pixel moved.
 */

type Gesture =
  | { type: "trim"; clipId: string; edge: "start" | "end"; sourceMs: number }
  | {
      type: "move";
      clipId: string;
      fromIndex: number;
      /** Pre-removal insertion index; `clips.length` means "after everything". */
      toIndex: number;
      started: boolean;
      /** Where the pointer went down — a move needs real travel to arm. */
      startX: number;
    }
  | { type: "scrub" };

type Props = {
  clips: Clip[];
  /** Real duration of each source, for trim clamps. */
  sourceDurations: Map<string, number>;
  sourceTitles: Map<string, string>;
  playheadMs: number;
  selectedId: string | null;
  chapterStarts: Array<{ startMs: number; title: string }>;
  onSelect: (clipId: string | null) => void;
  onChange: (clips: Clip[]) => void;
  onSeek: (outputMs: number) => void;
  onToggleMute: (clipId: string) => void;
  onSetTransition: (clipId: string, transition: Transition | undefined) => void;
};

/**
 * The three transitions, labelled honestly.
 *
 * "Wipe left" was verified against real ffmpeg: the incoming clip enters from
 * the right and the seam travels leftward — the picture is wiped *toward* the
 * left, which is what the name means to ffmpeg and now to us.
 */
const TRANSITION_LABEL = {
  crossfade: "transitions.crossfade",
  fadeblack: "transitions.fadeblack",
  wipeleft: "transitions.wipeleft",
} as const satisfies Record<Transition["type"], string>;

const TRANSITION_DURATIONS = [300, 500, 1000, 2000] as const;

/** Stable, muted hue per source so multi-source timelines read at a glance. */
function sourceHue(id: string): number {
  let hash = 0;
  for (const ch of id) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return Math.abs(hash) % 360;
}

const LANE_H = 64;
const SNAP_PX = 8;

export function Timeline({
  clips,
  sourceDurations,
  sourceTitles,
  playheadMs,
  selectedId,
  chapterStarts,
  onSelect,
  onChange,
  onSeek,
  onToggleMute,
  onSetTransition,
}: Props) {
  const { t } = useTranslation(["studio", "common"]);
  const f = useFormat();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pxPerSec, setPxPerSec] = useState(6);
  /** The clip whose incoming-transition menu is open, if any. */
  const [transitionMenuFor, setTransitionMenuFor] = useState<string | null>(null);
  const [gesture, setGesture] = useState<Gesture | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  gestureRef.current = gesture;
  /**
   * Where the pointer last was, surviving effect re-runs.
   *
   * The gesture effect re-creates on every setGesture (its deps include the
   * gesture), so a local `let` there is re-initialised mid-drag — which made
   * auto-scroll read the original press point whenever the pointer held
   * still at an edge, i.e. exactly when auto-scroll is needed. A ref set at
   * press and on every move is immune to the re-runs.
   */
  const lastPointerX = useRef<number | null>(null);

  const pxPerMs = pxPerSec / 1000;
  const toPx = (msValue: number) => msValue * pxPerMs;
  const toMs = useCallback((px: number) => px / pxPerMs, [pxPerMs]);

  /** Clips as drawn: the committed array with the live gesture applied. */
  const drawn = useMemo(() => {
    if (gesture?.type === "trim") {
      const clip = clips.find((c) => c.id === gesture.clipId);
      const max = clip ? (sourceDurations.get(clip.recordingId) ?? clip.sourceEndMs) : 0;
      return trimClip(clips, gesture.clipId, gesture.edge, gesture.sourceMs, max);
    }
    if (gesture?.type === "move" && gesture.started) {
      return moveClip(clips, gesture.fromIndex, gesture.toIndex);
    }
    return clips;
  }, [clips, gesture, sourceDurations]);

  const offsets = useMemo(() => outputOffsets(drawn), [drawn]);
  const durationMs = totalDurationMs(drawn);
  const widthPx = Math.max(toPx(durationMs) + 120, 400);

  /** Output positions worth snapping to: every clip boundary + the playhead. */
  const snapTargets = useMemo(() => {
    const targets = [0, playheadMs];
    drawn.forEach((clip, i) => {
      targets.push(offsets[i]!, offsets[i]! + clipDurationMs(clip));
    });
    return targets;
  }, [drawn, offsets, playheadMs]);

  const outputAtPointer = useCallback(
    (clientX: number) => {
      const strip = scrollRef.current;
      if (!strip) return 0;
      const rect = strip.getBoundingClientRect();
      return Math.max(0, toMs(clientX - rect.left + strip.scrollLeft));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pxPerMs],
  );

  // ── Global pointer tracking while a gesture is live ────────────────────────
  useEffect(() => {
    if (!gesture) return;

    /**
     * Auto-scroll while a gesture holds the pointer near an edge.
     *
     * Dragging a clip further than the visible strip was impossible: the
     * pointer hit the container's edge and the scroll position never moved,
     * so long timelines could not be reordered by drag at all. A rAF loop
     * nudges `scrollLeft` while the pointer sits in the edge zone — and
     * re-runs the gesture math, because scrolling moves the pointer in
     * timeline space even when the mouse is still.
     */
    let raf = 0;
    const EDGE = 48;
    const autoScroll = () => {
      const strip = scrollRef.current;
      const current = gestureRef.current;
      const pointerX = lastPointerX.current;
      if (strip && current && pointerX !== null) {
        const rect = strip.getBoundingClientRect();
        const past =
          pointerX < rect.left + EDGE
            ? pointerX - (rect.left + EDGE)
            : pointerX > rect.right - EDGE
              ? pointerX - (rect.right - EDGE)
              : 0;
        if (past !== 0) {
          strip.scrollLeft += Math.max(-24, Math.min(24, past / 3));
          updateFromPointer(pointerX);
        }
      }
      raf = requestAnimationFrame(autoScroll);
    };
    raf = requestAnimationFrame(autoScroll);

    const updateFromPointer = (clientX: number) => {
      const current = gestureRef.current;
      if (!current) return;
      const outputMs = outputAtPointer(clientX);

      if (current.type === "scrub") {
        onSeek(Math.min(outputMs, durationMs));
        return;
      }

      if (current.type === "trim") {
        const index = clips.findIndex((c) => c.id === current.clipId);
        if (index === -1) return;
        const clip = clips[index]!;
        // Convert the pointer's *output* position into this clip's source
        // time, snapping in output space where the eye judges alignment.
        const committedOffsets = outputOffsets(clips);
        const snapped = snap(outputMs, snapTargets, toMs(SNAP_PX));
        const sourceMs =
          clip.sourceStartMs + (snapped - committedOffsets[index]!);
        setGesture({ ...current, sourceMs });
        return;
      }

      // A move arms only after real travel: without a threshold, the 3px
      // twitch of grabbing a block committed a reorder the seam never showed.
      if (!current.started && Math.abs(clientX - current.startX) < 6) return;

      // Move: choose the insertion index from clip midpoints. Past the last
      // midpoint means "after everything" — clips.length, not length-1, or
      // the end slot is unreachable when dragging rightward.
      const committedOffsets = outputOffsets(clips);
      let toIndex = clips.length;
      for (let i = 0; i < clips.length; i++) {
        const mid = committedOffsets[i]! + clipDurationMs(clips[i]!) / 2;
        if (outputMs < mid) {
          toIndex = i;
          break;
        }
      }
      setGesture({ ...current, toIndex, started: true });
    };

    const onMove = (e: PointerEvent) => {
      lastPointerX.current = e.clientX;
      updateFromPointer(e.clientX);
    };

    const onUp = () => {
      lastPointerX.current = null;
      const current = gestureRef.current;
      if (current?.type === "trim") {
        const clip = clips.find((c) => c.id === current.clipId);
        const max = clip
          ? (sourceDurations.get(clip.recordingId) ?? clip.sourceEndMs)
          : 0;
        onChange(trimClip(clips, current.clipId, current.edge, current.sourceMs, max));
      }
      if (current?.type === "move" && current.started) {
        const next = moveClip(clips, current.fromIndex, current.toIndex);
        // Same-reference means the drag went nowhere — no history step.
        if (next !== clips) onChange(next);
      }
      setGesture(null);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [gesture, clips, durationMs, onChange, onSeek, outputAtPointer, snapTargets, sourceDurations, toMs]);

  // ── Zoom, genuinely anchored ───────────────────────────────────────────────
  const pxPerMsRef = useRef(pxPerMs);
  pxPerMsRef.current = pxPerMs;
  /** The timeline moment that must stay under this viewport x after a zoom. */
  const pendingAnchor = useRef<{ ms: number; px: number } | null>(null);

  /**
   * Zoom keeping a point still: the pointer for ctrl+wheel, the viewport
   * centre for the buttons. Without this, zooming moved the moment under the
   * cursor by seconds and you lost your place exactly when zooming in to
   * find it.
   */
  const zoomAnchored = useCallback((factor: number, clientX?: number) => {
    const strip = scrollRef.current;
    if (strip) {
      const rect = strip.getBoundingClientRect();
      const px = clientX !== undefined ? clientX - rect.left : rect.width / 2;
      pendingAnchor.current = {
        ms: (strip.scrollLeft + px) / pxPerMsRef.current,
        px,
      };
    }
    setPxPerSec((z) => Math.min(200, Math.max(1, z * factor)));
  }, []);

  // Applied after the new scale renders, so the anchor lands exactly.
  useLayoutEffect(() => {
    const strip = scrollRef.current;
    const anchor = pendingAnchor.current;
    if (strip && anchor) {
      strip.scrollLeft = Math.max(0, anchor.ms * pxPerMs - anchor.px);
      pendingAnchor.current = null;
    }
  }, [pxPerMs]);

  /** Scale so the whole timeline fits the strip, with a little margin. */
  const fit = useCallback(() => {
    const strip = scrollRef.current;
    if (!strip || durationMs === 0) return;
    strip.scrollLeft = 0;
    setPxPerSec(
      Math.min(200, Math.max(1, ((strip.clientWidth - 140) * 1000) / durationMs)),
    );
  }, [durationMs]);

  /**
   * Open at a readable scale. The default 6px/s rendered a 30-second edit as
   * a 180px ribbon in the corner of a 1400px strip, every clip name
   * truncated to one letter — and every reload forgot whatever zoom the
   * person had set. Fit once, when the duration first becomes known.
   */
  const fittedOnce = useRef(false);
  useEffect(() => {
    if (fittedOnce.current || durationMs === 0) return;
    fittedOnce.current = true;
    fit();
  }, [durationMs, fit]);

  /**
   * Wheel, attached natively and non-passively: React registers wheel
   * handlers passively, so `preventDefault` in an `onWheel` prop is ignored
   * and the page scrolls anyway. Ctrl+wheel zooms; a plain vertical wheel
   * pans the strip horizontally — it is a horizontal surface, and demanding
   * Shift for the only direction it can scroll was pure friction.
   */
  useEffect(() => {
    const strip = scrollRef.current;
    if (!strip) return;

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        zoomAnchored(e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX);
        return;
      }
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        strip.scrollLeft += e.deltaY;
      }
    };

    strip.addEventListener("wheel", onWheel, { passive: false });
    return () => strip.removeEventListener("wheel", onWheel);
  }, [zoomAnchored]);

  /** Ruler tick spacing that keeps labels readable at any zoom. */
  const tickMs = useMemo(() => {
    const candidates = [1000, 2000, 5000, 10_000, 30_000, 60_000, 300_000, 600_000];
    return candidates.find((c) => toPx(c) >= 70) ?? 600_000;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pxPerSec]);

  /** The clip whose transition bar is open — dropped if the clip was deleted. */
  const menuClip = transitionMenuFor
    ? clips.find((c) => c.id === transitionMenuFor) ?? null
    : null;

  const label = (msValue: number) => {
    const s = Math.floor(msValue / 1000);
    const m = Math.floor(s / 60);
    return m >= 60
      ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`
      : `${m}:${String(s % 60).padStart(2, "0")}`;
  };

  return (
    <div className="select-none">
      <div className="mb-1.5 flex items-center justify-between px-1">
        <span className="text-[12px] tabular-nums text-text-faint">
          {label(playheadMs)} / {label(durationMs)}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={fit}
            className="rounded px-2 py-1.5 text-[11px] text-text-muted hover:bg-surface-hover"
            title={t("timeline.fitTitle")}
          >
            {t("timeline.fit")}
          </button>
          <button
            type="button"
            onClick={() => zoomAnchored(1 / 1.4)}
            className="rounded px-2.5 py-1.5 text-[14px] leading-none text-text-muted hover:bg-surface-hover"
            title={t("timeline.zoomOut")}
          >
            −
          </button>
          <button
            type="button"
            onClick={() => zoomAnchored(1.4)}
            className="rounded px-2.5 py-1.5 text-[14px] leading-none text-text-muted hover:bg-surface-hover"
            title={t("timeline.zoomIn")}
          >
            +
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        // pan-x: horizontal touch panning stays native; gestures are mouse-only.
        className="relative overflow-x-auto overflow-y-hidden rounded-lg border border-border bg-bg-subtle [touch-action:pan-x]"
      >
        <div className="relative" style={{ width: widthPx }}>
          {/* Ruler — click or drag to seek. On touch, a tap seeks and a drag
              pans the strip: capturing the drag as a scrub would make the
              timeline impossible to scroll on a phone. */}
          <div
            className="relative h-6 cursor-pointer border-b border-border/60"
            onPointerDown={(e) => {
              if (e.pointerType === "touch") {
                onSeek(Math.min(outputAtPointer(e.clientX), durationMs));
                return;
              }
              e.preventDefault();
              lastPointerX.current = e.clientX;
              onSeek(Math.min(outputAtPointer(e.clientX), durationMs));
              setGesture({ type: "scrub" });
            }}
          >
            {Array.from({ length: Math.ceil(durationMs / tickMs) + 1 }, (_, i) => (
              <span
                key={i}
                className="absolute top-0 h-full border-l border-border/60 pl-1 font-mono text-[10px] leading-6 text-text-faint"
                style={{ left: toPx(i * tickMs) }}
              >
                {label(i * tickMs)}
              </span>
            ))}
          </div>

          {/* Chapter markers, above the clips they annotate. */}
          {chapterStarts.length > 0 ? (
            <div className="relative h-5 border-b border-border/40">
              {chapterStarts.map((chapter, i) => (
                <span
                  key={i}
                  dir="auto"
                  title={chapter.title}
                  className="absolute top-0 max-w-40 truncate border-l-2 border-accent pl-1 text-[10px] leading-5 text-text-muted"
                  style={{ left: toPx(chapter.startMs) }}
                >
                  {chapter.title}
                </span>
              ))}
            </div>
          ) : null}

          {/* Clip lane. */}
          <div className="relative" style={{ height: LANE_H + 12 }}>
            {drawn.map((clip, index) => {
              const isSelected = clip.id === selectedId;
              const isDragged =
                gesture?.type === "move" && gesture.clipId === clip.id && gesture.started;
              const hue = sourceHue(clip.recordingId);

              return (
                <div
                  key={clip.id}
                  role="button"
                  tabIndex={0}
                  onPointerDown={(e) => {
                    // Handles start their own gesture; the body starts a move.
                    if ((e.target as HTMLElement).dataset.trim) return;
                    // Touch selects and lets the strip pan. A finger cannot
                    // both scroll the timeline and drag a clip, and the
                    // accidental reorder is the worse of the two losses —
                    // reordering on a phone goes through select + toolbar.
                    if (e.pointerType === "touch") {
                      onSelect(clip.id);
                      return;
                    }
                    e.preventDefault();
                    lastPointerX.current = e.clientX;
                    onSelect(clip.id);
                    setGesture({
                      type: "move",
                      clipId: clip.id,
                      fromIndex: index,
                      toIndex: index,
                      started: false,
                      startX: e.clientX,
                    });
                  }}
                  className={cn(
                    "absolute top-1.5 overflow-hidden rounded-md border text-left transition-shadow",
                    isSelected
                      ? "border-accent shadow-[0_0_0_1px_var(--accent)]"
                      : "border-black/30",
                    isDragged && "opacity-70",
                  )}
                  style={{
                    left: toPx(offsets[index]!),
                    width: Math.max(toPx(clipDurationMs(clip)), 14),
                    height: LANE_H,
                    background: `hsl(${hue} 35% 32%)`,
                  }}
                >
                  <div className="pointer-events-none truncate px-2 pt-1 text-[11px] font-medium text-white/90">
                    {clip.label ?? sourceTitles.get(clip.recordingId) ?? t("timeline.clipFallback")}
                  </div>
                  <div className="pointer-events-none px-2 font-mono text-[10px] tabular-nums text-white/60">
                    {label(clip.sourceStartMs)}–{label(clip.sourceEndMs)}
                  </div>

                  {/* Muted: dimmed with a hatch, because colour alone is not a
                      state anyone can read back off a timeline. */}
                  {clip.muted ? (
                    <div
                      className="pointer-events-none absolute inset-0"
                      style={{
                        background:
                          "repeating-linear-gradient(-45deg, rgba(0,0,0,0.28) 0 6px, transparent 6px 12px)",
                      }}
                    />
                  ) : null}
                  <button
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleMute(clip.id);
                    }}
                    title={clip.muted ? t("timeline.unmuteClip") : t("timeline.muteClip")}
                    className={cn(
                      "absolute right-2.5 bottom-1 z-10 rounded p-0.5 transition-colors",
                      clip.muted
                        ? "bg-black/50 text-white"
                        : "text-white/35 hover:bg-black/40 hover:text-white",
                    )}
                  >
                    <VolumeX size={12} />
                  </button>

                  {/* Trim handles. Wide enough to hit, marked for the body's
                      pointerdown to ignore. */}
                  <div
                    data-trim="start"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      // Trim is a precision drag; on touch it fights the pan
                      // and an 8px handle is a guess. Select instead.
                      if (e.pointerType === "touch") {
                        onSelect(clip.id);
                        return;
                      }
                      e.preventDefault();
                      lastPointerX.current = e.clientX;
                      onSelect(clip.id);
                      setGesture({
                        type: "trim",
                        clipId: clip.id,
                        edge: "start",
                        sourceMs: clip.sourceStartMs,
                      });
                    }}
                    className="absolute inset-y-0 left-0 w-2 cursor-ew-resize bg-white/20 hover:bg-white/40"
                  />
                  <div
                    data-trim="end"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      if (e.pointerType === "touch") {
                        onSelect(clip.id);
                        return;
                      }
                      e.preventDefault();
                      lastPointerX.current = e.clientX;
                      onSelect(clip.id);
                      setGesture({
                        type: "trim",
                        clipId: clip.id,
                        edge: "end",
                        sourceMs: clip.sourceEndMs,
                      });
                    }}
                    className="absolute inset-y-0 right-0 w-2 cursor-ew-resize bg-white/20 hover:bg-white/40"
                  />
                </div>
              );
            })}

            {/* Transition chips, one per seam. A transition belongs to the
                clip it introduces, so the chip carries that clip's id.
                Positions are spaced a minimum 24px apart: around a short
                clip two chips landed on the same pixels and the later one
                swallowed every click on the earlier — an unclickable seam
                with nothing saying why. A nudged chip is still tied to its
                seam by its tooltip and the bar it opens. */}
            {(() => {
              if (gesture) return null;
              let lastX = Number.NEGATIVE_INFINITY;
              return drawn.map((clip, index) => {
                if (index === 0) return null;
                const transition = clip.transitionIn;
                const seamPx = Math.max(toPx(offsets[index]!), lastX + 24);
                lastX = seamPx;
                const effectiveMs = effectiveTransitionMs(drawn[index - 1], clip);
                const capped =
                  transition !== undefined && effectiveMs < transition.durationMs;

                return (
                  <button
                    key={`seam-${clip.id}`}
                    type="button"
                    data-transition-menu
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      setTransitionMenuFor((open) => (open === clip.id ? null : clip.id));
                    }}
                    title={
                      transition
                        ? capped
                          ? t("timeline.seamCapped", {
                              transition: t(TRANSITION_LABEL[transition.type]),
                              seconds: f.number(effectiveMs / 1000),
                              requested: f.number(transition.durationMs / 1000),
                            })
                          : t("timeline.seam", {
                              transition: t(TRANSITION_LABEL[transition.type]),
                              seconds: f.number(effectiveMs / 1000),
                            })
                        : t("timeline.addTransition")
                    }
                    className={cn(
                      "absolute bottom-0 z-10 h-[22px] min-w-[22px] -translate-x-1/2 rounded-full border px-1 font-mono text-[9.5px] leading-5",
                      transition
                        ? "border-accent bg-accent text-white"
                        : "border-border bg-surface text-text-faint hover:text-text",
                    )}
                    style={{ left: seamPx }}
                  >
                    {transition
                      ? t(`transitionsShort.${transition.type}`)
                      : "+"}
                  </button>
                );
              });
            })()}

            {/* Drop seam while reordering — only when the drop would land
                somewhere new, so the seam never promises a move that will
                not happen. */}
            {gesture?.type === "move" &&
            gesture.started &&
            moveClip(clips, gesture.fromIndex, gesture.toIndex) !== clips ? (
              <div
                className="absolute top-0 h-full w-0.5 bg-accent"
                style={{
                  // Past the last clip, the seam sits at the very end.
                  left: toPx(
                    outputOffsets(drawn)[gesture.toIndex] ?? totalDurationMs(drawn),
                  ),
                }}
              />
            ) : null}
          </div>

          {/* Playhead, over everything. */}
          <div
            className="pointer-events-none absolute top-0 bottom-0 w-px bg-red-400"
            style={{ left: toPx(Math.min(playheadMs, durationMs)) }}
          >
            <div className="-ml-[5px] h-0 w-0 border-x-[5px] border-t-[6px] border-x-transparent border-t-red-400" />
          </div>
        </div>
      </div>

      {/* Transition editor for the open seam. A bar under the strip rather
          than a popover: the strip scrolls and clips its overflow, and a menu
          that gets cut off at the edge of a scroll container is worse than no
          menu. */}
      {menuClip ? (
        <div
          data-transition-menu
          className="mt-1.5 flex flex-wrap items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1.5"
        >
          <span className="text-[11.5px] text-text-faint">
            <Trans
              t={t}
              i18nKey="timeline.transitionInto"
              values={{
                name:
                  menuClip.label ??
                  sourceTitles.get(menuClip.recordingId) ??
                  t("timeline.thisClip"),
              }}
              components={{ name: <span dir="auto" className="text-text-muted" /> }}
            />
          </span>
          <button
            type="button"
            onClick={() => onSetTransition(menuClip.id, undefined)}
            className={cn(
              "rounded-md border px-2 py-0.5 text-[11.5px]",
              !menuClip.transitionIn
                ? "border-accent text-text"
                : "border-border text-text-muted hover:text-text",
            )}
          >
            {t("timeline.cut")}
          </button>
          {TRANSITION_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() =>
                onSetTransition(menuClip.id, {
                  type,
                  durationMs: menuClip.transitionIn?.durationMs ?? 500,
                })
              }
              className={cn(
                "rounded-md border px-2 py-0.5 text-[11.5px]",
                menuClip.transitionIn?.type === type
                  ? "border-accent text-text"
                  : "border-border text-text-muted hover:text-text",
              )}
            >
              {t(TRANSITION_LABEL[type])}
            </button>
          ))}
          {menuClip.transitionIn ? (
            <>
              <span className="ml-1 text-[11.5px] text-text-faint">{t("timeline.for")}</span>
              {TRANSITION_DURATIONS.map((ms) => (
                <button
                  key={ms}
                  type="button"
                  onClick={() =>
                    onSetTransition(menuClip.id, {
                      type: menuClip.transitionIn!.type,
                      durationMs: ms,
                    })
                  }
                  className={cn(
                    "rounded-md border px-1.5 py-0.5 font-mono text-[11px] tabular-nums",
                    menuClip.transitionIn?.durationMs === ms
                      ? "border-accent text-text"
                      : "border-border text-text-muted hover:text-text",
                  )}
                >
                  {t("timeline.seconds", { seconds: f.number(ms / 1000) })}
                </button>
              ))}
            </>
          ) : null}
          {/* When the cap bites, say so with the real number — the header
              total was the only honest figure and it made people do the
              arithmetic themselves. */}
          {(() => {
            const index = clips.findIndex((c) => c.id === menuClip.id);
            const effective = effectiveTransitionMs(clips[index - 1], menuClip);
            const capped =
              menuClip.transitionIn !== undefined &&
              effective < menuClip.transitionIn.durationMs;
            return (
              <span className="ml-auto text-[11px] text-text-faint">
                {capped
                  ? t("timeline.capped", { seconds: f.number(effective / 1000) })
                  : t("timeline.capNote")}
              </span>
            );
          })()}
          <button
            type="button"
            onClick={() => setTransitionMenuFor(null)}
            className="rounded px-1.5 py-0.5 text-[12px] text-text-muted hover:bg-surface-hover"
            title={t("common:actions.close")}
          >
            ✕
          </button>
        </div>
      ) : null}
    </div>
  );
}
